// v2 — step-state builders for the appointment workflow stepper.
//
// Step state is derived from the appointment row's own `status` and the
// linked property's `marketing_stage`. Both are already on the page-level
// payload (appointments hook + properties hook), so no N+1 document fetch
// is needed for the MVP. If testers later want strict per-document state
// (e.g. "Schein is generated but NOT yet signed"), we can layer in a
// page-level `useDocumentsForAppointments(ids)` batched hook without
// changing the public API of buildAcquisitionSteps / buildViewingSteps.

import type { AppointmentRow } from '@/hooks/data/useAppointments';
import type { PropertyRow } from '@/hooks/data/useProperties';
import type { WorkflowStep } from './WorkflowStepper';

type TranslateFn = (key: string, fallback: string) => string;

const ACTIVE_OR_BEYOND: PropertyRow['marketing_stage'][] = [
  'active',
  'kaufanbot_angenommen',
  'notar_termin_geplant',
  'notariell_verkauft',
  'sold',
];
const OFFER_OR_BEYOND: PropertyRow['marketing_stage'][] = [
  'kaufanbot_angenommen',
  'notar_termin_geplant',
  'notariell_verkauft',
  'sold',
];
const NOTAR_OR_BEYOND: PropertyRow['marketing_stage'][] = [
  'notar_termin_geplant',
  'notariell_verkauft',
  'sold',
];
const SOLD: PropertyRow['marketing_stage'][] = ['notariell_verkauft', 'sold'];

function pickFirstCurrent(states: ('done' | 'future')[]): ('done' | 'current' | 'future')[] {
  const out: ('done' | 'current' | 'future')[] = states.slice();
  for (let i = 0; i < out.length; i++) {
    if (out[i] === 'future') {
      out[i] = 'current';
      break;
    }
  }
  return out;
}

export function buildAcquisitionSteps(
  appt: AppointmentRow,
  property: PropertyRow | undefined,
  t: TranslateFn,
): WorkflowStep[] {
  const meetingDone =
    appt.status === 'completed' || appt.status === 'signed_contract';
  const avSigned = !!property && ACTIVE_OR_BEYOND.includes(property.marketing_stage);
  const raw: ('done' | 'future')[] = [
    meetingDone ? 'done' : 'future',
    avSigned ? 'done' : 'future',
  ];
  const states = pickFirstCurrent(raw);
  return [
    { key: 'meeting', label: t('workflow.acq.1', 'Termin abgehalten'), state: states[0]! },
    { key: 'av', label: t('workflow.acq.2', 'AV unterzeichnet'), state: states[1]! },
  ];
}

export function buildViewingSteps(
  appt: AppointmentRow,
  property: PropertyRow | undefined,
  t: TranslateFn,
): WorkflowStep[] {
  const stage = property?.marketing_stage;
  const viewingDone = appt.status === 'completed' || appt.status === 'signed_contract';
  // Schein is treated as done whenever the viewing itself is marked
  // completed — the operator signs the confirmation in the same session.
  // If a separate doc-level check is needed later, swap to a doc fetch.
  const scheinDone = viewingDone;
  const offerDone = !!stage && OFFER_OR_BEYOND.includes(stage);
  const notarDone = !!stage && NOTAR_OR_BEYOND.includes(stage);
  const soldDone = !!stage && SOLD.includes(stage);
  const raw: ('done' | 'future')[] = [
    viewingDone ? 'done' : 'future',
    scheinDone ? 'done' : 'future',
    offerDone ? 'done' : 'future',
    notarDone ? 'done' : 'future',
    soldDone ? 'done' : 'future',
  ];
  const states = pickFirstCurrent(raw);
  return [
    { key: 'viewing', label: t('workflow.view.1', 'Besichtigung'), state: states[0]! },
    { key: 'schein',  label: t('workflow.view.2', 'Schein'),       state: states[1]! },
    { key: 'kaufanbot', label: t('workflow.view.3', 'Kaufanbot'),  state: states[2]! },
    { key: 'notar',   label: t('workflow.view.4', 'Notar'),        state: states[3]! },
    { key: 'sold',    label: t('workflow.view.5', 'Verkauft'),     state: states[4]! },
  ];
}
